import { useCallback, useMemo, useState, useEffect, lazy, Suspense } from 'react'
import { useRefState } from '@/hooks/use-ref-state'
import { COMPANION_CHAT_API } from '@/config/constants'
import { Outlet, useLocation } from 'react-router-dom'
import { NavSidebar } from './nav-sidebar'
import { TopBar } from './top-bar'
import { MobileNavContext, type MobileNavSide } from './mobile-nav-context'
import {
  Sheet,
  SheetContent,
  SheetContainerProvider,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { ErrorBoundary } from '@/components/common/error-boundary'
import { ImpersonationBanner } from '@/features/account/impersonation-banner'
import { NotificationToast } from '@/components/ui/notification-toast'
import { AlertNotifiers } from './alert-notifiers'
import { UpgradePromptHost } from '@/features/upgrade/upgrade-prompt-host'
import { createHtmlAudioSoundPlayer } from '@/features/scanner/alerts/audio/html-audio-sound-player'
import { useAudioUnlock } from '@/features/scanner/alerts/audio/use-audio-unlock'
import { useAlertEffects } from '@/features/scanner/alerts/use-alert-effects'
import { setCompanionActive, useCompanionActive } from '@/features/companion/companion-active-store'
import { DEFAULT_AUDIO, DEFAULT_VOLUME } from '@/features/scanner/alerts/audio/sound-catalog'
import { CompanionProvider, CompanionOverlay, useCompanionManager } from '@edge/edge-ai'
import { CompanionActionProvider } from '@/features/companion/companion-action-context'
import { useCompanionActionContext } from '@/features/companion/companion-action-hooks'
import { CompanionActionHost } from '@/features/companion/companion-action-host'
import { followSpanishQuestion } from '@/features/companion/language-follow'
import { AdminToolBridgeHost } from '@/features/companion/admin-tool-bridge-host'
import { CompanionWalkthrough } from '@/features/companion/companion-walkthrough'
import { CompanionHost } from '@/components/chat/companion-host'
import { RightRailHost } from '@/components/layout/right-rail-host'
import { ChartToolWheelHost } from '@/features/charts/chart-tool-wheel-host'
import { ChatBroadcastBridge } from '@/components/chat/chat-broadcast'
import { useChatStore } from '@/stores/chat-store'
import { useLanguageStore, languageName } from '@/stores/language-store'
import { VOICE_STYLE_PROMPT } from '@/features/companion/voice-style'
import { ASSISTANT_PERSONA, assistantFraming } from '@/features/companion/companion-personas'
import { buildDirectAnalystRoute } from '@/features/companion/direct-analyst-route'
import { trackAiTurn } from '@/features/companion/ai-turn-telemetry'
import { knowledgePrefetch } from '@/features/companion/knowledge-prefetch'
import { companionAuthToken } from '@/features/companion/companion-auth-token'

import { useThemeServerSync } from '@/features/theme-editor/use-theme-server-sync'
import { useTextScaleServerSync } from '@/features/theme-editor/use-text-scale-server-sync'
import { useFilingReadingModeServerSync } from '@/features/research/filings/use-filing-reading-mode-server-sync'
import { useChartInteractionServerSync } from '@/features/charts/use-chart-interaction-server-sync'
import { useChartIndicatorDefaultsServerSync } from '@/features/charts/use-chart-indicator-defaults-server-sync'
import { useLearnProgressServerSync } from '@/features/learn/use-learn-progress-server-sync'
import { useOnboardingServerSync } from '@/features/onboarding/use-onboarding-server-sync'
import { useCompanionAiDebug } from '@/ett-debug-tools/ai/use-companion-ai-debug'
import { openLiveSpeech } from '@/features/companion/live-speech'
import { collectSttHints } from '@/components/chat/voice/stt-hints'

function setDetectedLanguage(manager: ReturnType<typeof useCompanionManager>, language: string) {
  manager.detectedLanguage = language
}

/** Connects the CompanionOverlay to the frontend action context (approve/revert). */
function CompanionOverlayWithActions({
  onActiveChange,
}: {
  onActiveChange: (active: boolean) => void
}) {
  const { pendingAction, approveAction, revertAction } = useCompanionActionContext()
  const manager = useCompanionManager()
  // Dev-only: stream each LLM round into the debug sidebar (latency + i/o).
  useCompanionAiDebug(manager)
  const language = useLanguageStore((s) => s.language)
  const companionOn = useCompanionActive()
  const chatOpen = useChatStore((s) => s.open)
  // One assistant (2026-10-16): both send paths (chat + voice) frame the
  // prompt with its block, and the turn check decides per turn whether it may
  // change the workspace, on every plan.
  useEffect(() => {
    manager.setPersona(ASSISTANT_PERSONA)
    manager.setPersonaFraming(assistantFraming())
  }, [manager])
  // Mirror the language preference so the AI always answers in it: the English
  // name drives the prompts; the code drives TTS voice selection.
  useEffect(() => {
    manager.setLanguage(languageName(language), true)
    setDetectedLanguage(manager, language)
  }, [manager, language])
  return (
    <CompanionOverlay
      enabled={companionOn}
      onEnabledChange={setCompanionActive}
      onActiveChange={onActiveChange}
      // One place for a pending change (2026-10-15): the conversation when the
      // chat is open; the overlay only mirrors it while the chat is closed.
      pendingAction={chatOpen ? null : pendingAction}
      onApproveAction={approveAction ?? undefined}
      onRevertAction={revertAction ?? undefined}
    />
  )
}

const SIDEBAR_WIDTH = 50

const debugToolsEnabled = import.meta.env.DEV || import.meta.env.VITE_ENABLE_DEBUG_TOOLS === 'true'

// Dev/staging-only: lazy-loaded debug sidebar. In production builds without
// VITE_ENABLE_DEBUG_TOOLS, the ternary collapses to `null` at build time and
// Rollup drops the dynamic import chunk entirely. Import the UI directly
// (bypassing the barrel) so it can't accidentally leak into consumers of
// `@/ett-debug-tools`.
const DebugSidebar = debugToolsEnabled
  ? lazy(() => import('@/ett-debug-tools/debug-sidebar').then((m) => ({ default: m.DebugSidebar })))
  : null

/**
 * Main app layout: narrow icon-only sidebar (desktop) + sheet sidebar (mobile) + content area.
 */
export function AppLayout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  // Which edge the nav sheet slides in from: the generic mobile top bar keeps
  // its hamburger on the right, a page-owned top line puts it on the left.
  const [mobileSide, setMobileSide] = useState<MobileNavSide>('right')
  // Pages that paint their own top line below `md` (hamburger + Edge mark
  // inline with their header) claim the top bar so the generic one stays
  // hidden and nothing stacks into a second line. Counted, not boolean, so
  // overlapping mounts during route transitions cannot flash it back.
  const [topBarClaims, setTopBarClaims] = useState(0)
  const openNav = useCallback((side: MobileNavSide = 'right') => {
    setMobileSide(side)
    setMobileOpen(true)
  }, [])
  const claimTopBar = useCallback(() => {
    setTopBarClaims((n) => n + 1)
    return () => setTopBarClaims((n) => n - 1)
  }, [])
  // Surfaces that own the whole viewport (the first-run survey) claim the
  // chrome; counted for the same reason as the top bar.
  const [chromeClaims, setChromeClaims] = useState(0)
  const claimChrome = useCallback(() => {
    setChromeClaims((n) => n + 1)
    return () => setChromeClaims((n) => n - 1)
  }, [])
  const mobileNav = useMemo(
    () => ({ openNav, claimTopBar, claimChrome }),
    [openNav, claimTopBar, claimChrome],
  )
  const { pathname } = useLocation()
  // Sync the active theme and text size with the user's server profile (cross-device).
  useThemeServerSync()
  useTextScaleServerSync()
  useFilingReadingModeServerSync()
  // Sync the global chart interaction settings with the user's server
  // profile (cross-device).
  useChartInteractionServerSync()
  // Sync per-indicator saved defaults ("Save current as Default") with the
  // user's server profile (cross-device).
  useChartIndicatorDefaultsServerSync()
  // Sync Learn curriculum progress with the user's server profile (cross-device).
  useLearnProgressServerSync()
  // Sync first-run onboarding state (answers/persona/checklist, cross-device).
  useOnboardingServerSync()
  // The strategy workspace owns its whole viewport: it paints the Edge mark in
  // its own header (at the nav rail's exact geometry) and its mode strip along
  // the bottom. Leaving the global rail up would put two Edge logos on screen
  // and steal the width the strip and the canvas need.
  // The first-run onboarding survey claims it too.
  const hideChrome =
    pathname.startsWith('/backtester/editor') ||
    /^\/backtester\/strategy\/[^/]+/.test(pathname) ||
    chromeClaims > 0
  // Stable ref callback for the main content container. Must be stable
  // because `mainContentEl` flows into `<SheetContainerProvider>` → Radix
  // Portal `container` prop, and any `el → null → el` churn would unmount
  // and remount every portaled Sheet (column picker, filter picker, etc.).
  // See `use-ref-state.ts` for the full incident write-up.
  const [setMainContentRef, mainContentEl] = useRefState<HTMLDivElement>()

  const sound = createHtmlAudioSoundPlayer({ blocking: true, volume: DEFAULT_VOLUME })
  // Sound pings are global; each scanner owns its TTS announcement queue.
  useAudioUnlock(sound)
  useAlertEffects({
    sound,
    cooldownMs: 250,
    defaultSoundId: DEFAULT_AUDIO,
  })

  // Panel TTS queues and news readout subscribe to the same companion mute.
  const handleCompanionActive = (active: boolean) => {
    sound.setMuted(active)
    // Publish for panel-level audio (the news readout) so everything that
    // can talk ducks off the same signal instead of inventing its own.
    setCompanionActive(active)
  }

  return (
    <CompanionProvider
      config={{
        apiBase: COMPANION_CHAT_API,
        tts: true,
        // Refresh when the access token is missing/stale instead of sending
        // an empty Bearer (which 401s instantly — 2026-08-24 incident).
        getAuthToken: companionAuthToken,
        // Same ticker and vocabulary hints the composer voice session sends.
        getSttKeyterms: collectSttHints,
        // A Spanish push-to-talk question flips the language option first.
        onHeardText: followSpanishQuestion,
        // Data questions stream straight from the analyst (ack ~1s, tokens
        // ~2-3s) instead of the chat-hop -> tool -> re-summary path (~10s).
        directDataRoute: buildDirectAnalystRoute(),
        // A concept turn's search (the glossary and the columns) runs alongside
        // the turn check, so its first model round can answer (2026-10-18).
        knowledgePrefetch,
        // Voice replies from the chat model are spoken sentence by sentence
        // as they stream, not after the whole reply (2026-10-02).
        liveSpeech: openLiveSpeech,
        // Speech is salience-first concise; chat keeps the effort-chip depth.
        voiceStyle: VOICE_STYLE_PROMPT,
        // One ai_turn analytics event per turn (TEL, 2026-10-19).
        onTurnTelemetry: trackAiTurn,
      }}
    >
      <CompanionActionProvider>
        <MobileNavContext value={mobileNav}>
          <div className="flex h-screen flex-col overflow-hidden bg-background">
            <ImpersonationBanner />
            <NotificationToast />
            {/* Price-alert toasts over edge-realtime (staging build gate). */}
            <AlertNotifiers sound={sound} />
            {/* The upgrade pitch any plan gate raises. Mounted once, here, so
                  a refusal anywhere in the app opens the same dialog. */}
            <UpgradePromptHost />
            <div className="flex flex-1 overflow-hidden">
              {/* Sheet container: covers sidebar + content, but not the chat drawer */}
              <div ref={setMainContentRef} className="relative flex flex-1 overflow-hidden">
                <SheetContainerProvider container={mainContentEl}>
                  {/* Desktop sidebar */}
                  {!hideChrome && (
                    <aside
                      className="hidden md:flex md:flex-col"
                      style={{ width: SIDEBAR_WIDTH, minWidth: SIDEBAR_WIDTH }}
                    >
                      <NavSidebar />
                    </aside>
                  )}

                  {/* Mobile sidebar */}
                  {!hideChrome && (
                    <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
                      <SheetContent side={mobileSide} className="w-64 p-0">
                        <SheetTitle className="sr-only">Navigation</SheetTitle>
                        <SheetDescription className="sr-only">
                          Main navigation menu
                        </SheetDescription>
                        <NavSidebar expanded onNavigate={() => setMobileOpen(false)} />
                      </SheetContent>
                    </Sheet>
                  )}

                  {/* Main content area */}
                  <div className="flex flex-1 flex-col overflow-hidden">
                    {!hideChrome && topBarClaims === 0 && (
                      <TopBar onMenuClick={() => openNav('right')} />
                    )}

                    <main className="flex flex-1 flex-col overflow-hidden">
                      <ErrorBoundary>
                        <Outlet />
                      </ErrorBoundary>
                    </main>
                  </div>
                </SheetContainerProvider>
              </div>

              {/* Panel config rail + companion surfaces (drawer + floating
                  window): outside the sheet container so sheets don't cover
                  them. The rail and the drawer share one width and swap in
                  place (right-rail-store). */}
              <RightRailHost />
              <CompanionHost />
            </div>

            {/* BroadcastChannel bridge for pop-out chat — always active */}
            <ChatBroadcastBridge />

            {/* Global action host — single subscriber to tool-request events */}
            <CompanionActionHost />
            {/* The admin portal's Tools page runs browser tools through it (off unless configured) */}
            <AdminToolBridgeHost />
            {/* The drawing tool wheel: one for the app, hold Tab over a chart */}
            <ChartToolWheelHost />
            {/* AI Companion overlay */}
            <CompanionOverlayWithActions onActiveChange={handleCompanionActive} />
            {/* Guided walkthrough overlay (deterministic, LLM-free pointing) */}
            <CompanionWalkthrough />
          </div>
          {DebugSidebar && (
            <Suspense fallback={null}>
              <DebugSidebar />
            </Suspense>
          )}
        </MobileNavContext>
      </CompanionActionProvider>
    </CompanionProvider>
  )
}
